import React, { useState, useEffect } from 'react';
import { WifiOff, RefreshCw } from 'lucide-react';
import { API_URL } from '../../utils/utils';
import './NetworkStatusOverlay.css';

const NetworkStatusOverlay = () => {
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const [isChecking, setIsChecking] = useState(false);

  //Check Connection is not possible
  const checkConnection = async () => {
    // Browser itself says internet is disconnected
    if (!navigator.onLine) {
      setIsOffline(true);
      return;
    }

    setIsChecking(true);

    try {
      const controller = new AbortController();

      const timeoutTimer = setTimeout(() => {
        controller.abort();
      }, 10000);

      const pingUrl = API_URL
        ? `${API_URL.replace(/\/+$/, '')}/health`
        : '/favicon.ico';

      await fetch(pingUrl, {
        method: 'GET',
        signal: controller.signal,
        cache: 'no-store',
      });

      clearTimeout(timeoutTimer);

      // Internet is available
      setIsOffline(false);
    } catch (error) {
      /*
       * API/server error should NOT show the offline popup.
       * We only check the actual browser network status.
       */
      setIsOffline(!navigator.onLine);
    } finally {
      setIsChecking(false);
    }
  };

  useEffect(() => {
    checkConnection();

    const handleOnline = () => {
      setIsOffline(false);
      checkConnection();
    };

    const handleOffline = () => {
      setIsOffline(true);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const handleRetry = () => {
    checkConnection();
  };

  // Popup only when internet connection is unavailable
  if (!isOffline) return null;

  return (
    <div className="network-overlay-container">
      <div className="network-overlay-backdrop" />

      <div className="network-overlay-card">
        <div className="network-overlay-card-glow" />

        <div className="network-icon-container">
          <div className="network-icon-pulse offline">
            <WifiOff className="network-icon" size={48} />
          </div>
        </div>

        <h2 className="network-overlay-title">
          No Internet Connection
        </h2>

        <p className="network-overlay-description">
          It looks like you are not connected to the internet.
          Please check your internet connection and try again.
        </p>

        <div className="network-overlay-actions">
          <button
            type="button"
            className="network-btn btn-primary"
            onClick={handleRetry}
            disabled={isChecking}
          >
            {isChecking ? (
              <>
                <RefreshCw className="spinner-icon" size={18} />
                Checking...
              </>
            ) : (
              'Retry Connection'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default NetworkStatusOverlay;